import { useState } from 'react'
import { Badge, Button, Text } from '@mantine/core'
import { IconPencil, IconPlayerPlay } from '@tabler/icons-react'
import { EditTaskModal } from './EditTaskModal'
import { RunTaskModal } from './RunTaskModal'
import { TaskArgsView } from './TaskArgsView'
import { formatDateTime, type RunSelection, type TaskInfo } from './hooks'

function Field({
  label,
  className = '',
  children,
}: {
  label: string
  className?: string
  children: React.ReactNode
}) {
  return (
    <div className={`min-w-0 ${className}`}>
      <Text size="xs" c="dimmed" tt="uppercase">
        {label}
      </Text>
      <div className="truncate text-sm">{children}</div>
    </div>
  )
}

interface TaskInfoPanelProps {
  task: TaskInfo | null
  taskCount: number
  /** Called with the freshly scheduled run after the Run dialog succeeds. */
  onRunScheduled: (selection: RunSelection) => void
}

/** Top panel: the selected task's row fields, or the all-tasks placeholder. */
export function TaskInfoPanel({ task, taskCount, onRunScheduled }: TaskInfoPanelProps) {
  const [runModalOpen, setRunModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  if (!task) {
    return (
      <div className="flex h-full items-center justify-center">
        <Text c="dimmed">
          Showing runs for all {taskCount} task{taskCount === 1 ? '' : 's'}
        </Text>
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto px-4 py-3">
      <div className="mb-2 flex items-center gap-2">
        <Text fw={600}>{task.name}</Text>
        <Text size="sm" c="dimmed" ff="monospace">
          {task.slug}
        </Text>
        <Badge color={task.status === 'active' ? 'green' : 'gray'} variant="light">
          {task.status}
        </Badge>
        <Badge color={task.runnerPresent ? 'green' : 'red'} variant="dot">
          {task.runnerPresent ? 'runner present' : 'no runner'}
        </Badge>
        <div className="ml-auto flex gap-2">
          <Button
            size="compact-sm"
            variant="default"
            leftSection={<IconPencil size={14} />}
            onClick={() => setEditModalOpen(true)}
          >
            Edit
          </Button>
          <Button
            size="compact-sm"
            variant="light"
            leftSection={<IconPlayerPlay size={14} />}
            // A disabled task launches nothing — the server would reject the run anyway.
            disabled={task.status === 'disabled'}
            onClick={() => setRunModalOpen(true)}
          >
            Run
          </Button>
        </div>
      </div>
      {runModalOpen && (
        // Mounted fresh per open so the dialog re-seeds from the task's scheduled_args.
        <RunTaskModal
          task={task}
          onClose={() => setRunModalOpen(false)}
          onScheduled={onRunScheduled}
        />
      )}
      {editModalOpen && (
        // Mounted fresh per open so the form re-seeds from the current task row.
        <EditTaskModal task={task} onClose={() => setEditModalOpen(false)} />
      )}
      <div className="grid grid-cols-4 gap-x-6 gap-y-2 xl:grid-cols-6">
        <Field label="Schedule">
          {task.schedule ? (
            <span className={task.onSchedule ? '' : 'text-gray-400 line-through'}>
              {task.schedule} UTC
            </span>
          ) : (
            'on demand only'
          )}
        </Field>
        <Field label="Next run">{formatDateTime(task.nextRunAt)}</Field>
        <Field label="Last heartbeat">{formatDateTime(task.heartbeatAt)}</Field>
        <Field label="Log level">{task.logLevel}</Field>
        <Field label="Concurrency">
          {task.maxConcurrentRuns} total / {task.maxInstanceRuns} per instance
        </Field>
        <Field label="Followup action">{task.followupAction}</Field>
        <Field label="Created">{formatDateTime(task.createdAt)}</Field>
        <Field label="Updated">
          {formatDateTime(task.updatedAt)}
          {task.updatedBy ? ` by ${task.updatedBy}` : ''}
        </Field>
        <Field label="Scheduled args" className="col-span-2">
          <TaskArgsView slug={task.slug} value={task.scheduledArgs} />
        </Field>
      </div>
    </div>
  )
}
